import OptionalContainer, { OptionalList } from 'components/OptionalContainer';
import { ScArrowLink, type ValidSitecoreLink } from 'components/SitecoreLink';
import Text from 'components/Text';
import { ThemedBlockContainerNew } from 'components/ThemedBlockContainer';
import { getThemeProps, type ThemeName } from 'design-system/themes';
import type { HeadingLevel } from 'models/sitecore';
import { cn, cnm } from 'utils/classNames';

export type Alignment = 'center' | 'fill' | 'hug';
export type HighlightTextSize = 'lg' | 'md' | 'sm' | 'xl' | 'xs';

const highlightSizeClasses = {
	xs: 'highlightXs',
	sm: 'highlightSm',
	md: 'highlightMd',
	lg: 'highlightLg',
	xl: 'highlightXl',
} as const satisfies Record<HighlightTextSize, string>;

interface Props {
	alignment?: Alignment;
	className?: string;
	content?: string;
	eyebrow?: string;
	hasThemePadding?: boolean;
	heading?: string;
	headingLevel?: HeadingLevel;
	highlightText?: string;
	highlightTextSize?: HighlightTextSize;
	innerClassName?: string;
	links?: ValidSitecoreLink[];
	/** If a ribbon should be shown when the theme has one. */
	showRibbon?: boolean;
	theme?: ThemeName;
}

export default function ContentTextItem({
	alignment = 'center',
	className,
	content,
	eyebrow,
	hasThemePadding = true,
	heading,
	headingLevel = 'h2',
	highlightText,
	highlightTextSize = 'xs',
	innerClassName,
	links,
	showRibbon = true,
	theme,
}: Props) {
	const themeProps = getThemeProps(theme);

	const children = (
		<>
			{eyebrow && (
				<Text as="p" styleAs="h3">
					{eyebrow}
				</Text>
			)}
			{highlightText && (
				<Text
					as={highlightSizeClasses[highlightTextSize]}
					className={cn('mt-2 font-heading', themeProps.highlightTextClass)}
				>
					{highlightText}
				</Text>
			)}
			<Text as={headingLevel} className={cn(!highlightText && 'mt-2')}>
				{heading}
			</Text>
			{content && (
				<Text as="div" className="mt-2">
					{content}
				</Text>
			)}
			{links && (
				<OptionalList
					// Padding top instead of margin to keep the spacing when
					// auto margin is set for fill.
					className={cn('space-y-4 pt-4', alignment === 'fill' && 'mt-auto')}
					items={links.map((link) => (
						<ScArrowLink
							key={link.href}
							link={link}
							variant={themeProps.arrowLinkVariant}
						/>
					))}
				/>
			)}
		</>
	);
	const layoutClasses = cn(
		'flex flex-col',
		alignment === 'center' && 'justify-center',
		alignment === 'hug' && 'justify-center',
	);

	return (
		<ThemedBlockContainerNew
			theme={theme}
			className={cnm(
				'flex',
				themeProps.isPadded && hasThemePadding && 'p-r-md',
				!innerClassName && layoutClasses,
				className,
			)}
			withRibbonClassName="py-r-xl"
			showRibbon={showRibbon}
		>
			<OptionalContainer
				as="div"
				hasContainer={Boolean(innerClassName)}
				className={cnm('grow', layoutClasses, innerClassName)}
			>
				{children}
			</OptionalContainer>
		</ThemedBlockContainerNew>
	);
}
ContentTextItem.displayName = 'ContentTextItem';
